import { LBMCHeader } from "@lbmc-digital/lbmc-ui-kit";
import ICONS from "../Constants/Icons.tsx";
import React, { useState, useEffect, useRef } from "react";
import {
  useParams,
  useLocation,
  useNavigationType,
  useNavigate,
} from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import {
  getShareholderDetails,
  GetAvailablePlans,
} from "../Services/EvaluationPlanDashboardService.tsx";
import useAccessToken from "../Custom/useAccessToken.tsx";
import { useUserContext } from "../Context/UserContext.tsx";
import SessionService from "../Services/SessionService.tsx";
import { useTheme } from "../ThemeContext/ThemeContext.tsx";
import Apexlogo from "../assets/images/Apex-Logo_dark.svg";
import { getLastRefreshDate } from "../Services/DateService.tsx";
import { getCurrentFiscalYearDetails } from "../Services/FiscalYearService.tsx";
import { getFiscalYearDetailsById } from "../Services/EvaluationPlanDashboardService.tsx";
import moment from "moment";
import MGTProfileCard from "../Components/MGTProfileCard.tsx";
import useProfilePhoto from "../Custom/useProfilePhoto";
import { useBrokenLinkDialog } from "../Context/BrokenLinkDialogContext.tsx";
import { useShareholderContext } from "../Context/ShareholderContext.tsx";
import { useNetwork } from "../Context/NetworkProvider.tsx";
import {
  getGrowthFiscalYearList,
  getGrowthPersonalLeader,
  getGrowthUserDetailsForLeader,
} from "../Services/LeaderDashboardService.tsx";

export default function Header() {
  const { isDarkMode } = useTheme();
  const Icon = ICONS();
  const { accessToken } = useAccessToken();
  const { showApiError } = useNetwork();
  const { shareholderId } = useParams<{ shareholderId: any }>();
  const { currentFiscalYearId } = useParams<{ currentFiscalYearId: any }>();
  const {
    apexUserId,
    fiscalYearId: growthReporteeFiscalYearId,
    growthFiscalYearId,
  } = useParams<{
    apexUserId?: string;
    fiscalYearId?: string;
    growthFiscalYearId?: string;
  }>();
  const [shareholderName, setShareholderName] = useState<string | null>(null);
  const [shLeaderName, setLeaderName] = useState<string | null>(null);
  const [shareholderEntraIDForSLL, setShareholderEntraID] = useState<
    string | null
  >(null);

  const [currentShareholderId, setCurrentShareholderId] =
    useState<string | null>(null);
  const [leaderEntraIdForSLL, setLeaderEntraId] = useState<string | null>(null);
  const [currentFY, setCurrentFY] = useState<string | null>(null);
  const [plans, setPlans] = useState<
    Array<{ label: string; value: string; status?: string }>
  >([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [growthFiscalYears, setGrowthFiscalYears] = useState<any[]>([]);
  const [selectedGrowthFiscalYearId, setSelectedGrowthFiscalYearId] =
    useState<string | null>(null);
  const [growthLeaderName, setGrowthLeaderName] = useState<string | null>(null);
  const [growthLeaderEntraId, setGrowthLeaderEntraId] = useState<string | null>(null);
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [plansReloadToken, setPlansReloadToken] = useState(0);
  const closureTriggeredRef = useRef(false);
  const [headerElements, setHeaderElements] = useState<any[]>([]);
  const { username, userId, leaderName, leaderEntraId } = useUserContext();
  const navigateTo = SessionService.getNavigateTo();
  const { graphtoken } = useProfilePhoto();

  const [lastRefreshDate, setLastRefreshDate] = useState<string | null>(null);

  const location = useLocation();
  const isGrowthPage = location.pathname.startsWith("/growth");
  const isEvaluationPlanPage = location.pathname.startsWith("/evaluationPlan");
  const isGrowthReporteePage = location.pathname.startsWith("/growth/reportee");
  const navigationType = useNavigationType();
  const { throughWCvisible, setThroughWCvisible, showDialog } =
    useBrokenLinkDialog();
  const { setShareholderInfo } = useShareholderContext();

  useEffect(() => {
    if (location.pathname !== "/") return;

    setShareholderInfo({ shareholderName: null, shareholderId: null });
    setShareholderName(null);
    setLeaderName(null);
    setShareholderEntraID(null);
    setLeaderEntraId(null);
    setCurrentShareholderId(null);
  }, [location.pathname, setShareholderInfo]);

  useEffect(() => {
    if (navigationType === "POP") {
      // if navigating to Leader landing page from Evaluation plan page
      if (location.pathname == "/") {
        SessionService.setNavigateTo("LeaderLandingPage");
      } else if (location.pathname?.includes("/evaluationPlan")) {
        SessionService.setNavigateTo("EvaluationPlan");
      }
    }
  }, [location, navigationType]);

  useEffect(() => {
    // Manual dropdown choice should not persist after leaving Evaluation Plan.
    if (!location.pathname?.includes("/evaluationPlan")) {
      setIsManualSelection(false);
      setSelectedPlan(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    // when shareholderId & currentFiscalYearId comming from url params
    if (
      accessToken &&
      shareholderId &&
      currentFiscalYearId &&
      !throughWCvisible
    ) {
      fetchSHDetails(accessToken, shareholderId, currentFiscalYearId);
    }

    // when there is no value in url param, then get SH & Leader name from context (for logged in Leader/SH)
    if (!shareholderId && !currentFiscalYearId) {
      // below context will help to set shareholder's details (whose plan is opend)
      setShareholderInfo({
        shareholderName: username ? username : null,
        shareholderId: userId ? userId : null,
      });
      setShareholderName(username ? username : null);
      setLeaderName(leaderName ? leaderName : null);
      setShareholderEntraID(userId ? userId : null);
      setLeaderEntraId(leaderEntraId ? leaderEntraId : null);
    }

    addHeaderElementBasedOnPage();
  }, [accessToken, shareholderId, currentFiscalYearId, location.pathname]);

  useEffect(() => {
    if (!isGrowthPage) return;

    if (!isGrowthReporteePage || !apexUserId || !growthReporteeFiscalYearId) {
      setShareholderInfo({
        shareholderName: username || null,
        shareholderId: userId || null,
      });
      setShareholderName(username || null);
      setLeaderName(leaderName || null);
      setShareholderEntraID(userId || null);
      setLeaderEntraId(leaderEntraId || null);
      return;
    }

    Promise.all([
      getGrowthUserDetailsForLeader(apexUserId, growthReporteeFiscalYearId),
      getFiscalYearDetailsById(growthReporteeFiscalYearId),
    ])
      .then(([growthUser, fiscalYear]) => {
        setShareholderInfo({
          shareholderName: growthUser.name,
          shareholderId: growthUser.apexUserId,
        });
        setShareholderName(growthUser.name);
        setLeaderName(growthUser.leaderName || null);
        setShareholderEntraID(growthUser.entraObjectId || null);
        setLeaderEntraId(null);
        setCurrentFY(fiscalYear?.actualYear || null);
      })
      .catch((error) => {
        console.error("Error fetching growth user details:", error);
        setShareholderName(null);
        setLeaderName(null);
      });
  }, [
    apexUserId,
    growthReporteeFiscalYearId,
    isGrowthPage,
    isGrowthReporteePage,
    leaderEntraId,
    leaderName,
    userId,
    username,
  ]);

  useEffect(() => {
    if (!isGrowthPage) {
      setGrowthFiscalYears([]);
      setSelectedGrowthFiscalYearId(null);
      return;
    }

    let cancelled = false;

    getGrowthFiscalYearList()
      .then((response) => {
        if (cancelled) return;

        const currentGrowthFY = (response || []).find(
          (fiscalYear: any) => fiscalYear.isCurrent,
        )?.actualYear;
        const isCurrentGrowthFYClosed = (response || []).some(
          (fiscalYear: any) =>
            fiscalYear.isCurrent && fiscalYear.disabled,
        );
        const filteredGrowthFiscalYears = (response || []).filter(
          (fiscalYear: any) =>
            fiscalYear.actualYear <= currentGrowthFY ||
            isCurrentGrowthFYClosed,
        );

        const mapped = filteredGrowthFiscalYears.map((fiscalYear: any) => {
          const statusLabel = fiscalYear.disabled ? "Closed" : "Active";

          return {
            label: `FY ${fiscalYear.actualYear} Plan (${statusLabel})`,
            value: fiscalYear.fiscalYearId,
            actualYear: fiscalYear.actualYear,
            isCurrent: fiscalYear.isCurrent,
            status: fiscalYear.disabled ? "closed" : "active",
          };
        });

        setGrowthFiscalYears(mapped);

        const routeFiscalYearId = isGrowthReporteePage
          ? growthReporteeFiscalYearId
          : growthFiscalYearId;
        const selected =
          mapped.find((fiscalYear: any) => fiscalYear.value === routeFiscalYearId) ||
          mapped.find((fiscalYear: any) => fiscalYear.isCurrent) ||
          mapped[0];

        if (selected) {
          setSelectedGrowthFiscalYearId(selected.value);
          setCurrentFY(String(selected.actualYear));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error loading Growth fiscal years:", error);
          setGrowthFiscalYears([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    growthFiscalYearId,
    growthReporteeFiscalYearId,
    isGrowthPage,
    isGrowthReporteePage,
  ]);

  useEffect(() => {
    if (!isGrowthPage || isGrowthReporteePage || !selectedGrowthFiscalYearId) {
      setGrowthLeaderName(null);
      setGrowthLeaderEntraId(null);
      return;
    }

    let cancelled = false;
    setGrowthLeaderName(null);
    setGrowthLeaderEntraId(null);

    getGrowthPersonalLeader(selectedGrowthFiscalYearId)
      .then((leader) => {
        if (cancelled) return;

        setGrowthLeaderName(leader.leaderName || null);
        setGrowthLeaderEntraId(leader.leaderEntraObjectId || null);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error loading personal Growth leader:", error);
          setGrowthLeaderName(null);
          setGrowthLeaderEntraId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isGrowthPage, isGrowthReporteePage, selectedGrowthFiscalYearId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isGrowthReporteePage) return;

      const fiscalYearDetails = SessionService.getFiscalYearDetails();
      if (fiscalYearDetails) {
        setCurrentFY(fiscalYearDetails.actualYear);
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [isGrowthReporteePage]);

  useEffect(() => {
    const handlePlanClosureUpdated = () => {
      setIsManualSelection(false);
      closureTriggeredRef.current = true;
      setPlansReloadToken((prev) => prev + 1);
    };

    globalThis.addEventListener(
      "plan-closure-updated",
      handlePlanClosureUpdated,
    );
    return () =>
      globalThis.removeEventListener(
        "plan-closure-updated",
        handlePlanClosureUpdated,
      );
  }, []);

  // initialise available plans (replace with real service call if available)
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const shareholderIdForPlans = shareholderId ?? userId;

        // Only fetch if we have a valid shareholderId
        if (!shareholderIdForPlans) {
          return;
        }

        const resp = await GetAvailablePlans(shareholderIdForPlans);

        const currentFYPlanDetails = resp.find((p: any) => p.isCurrentYearPlan);
        const currentFY = currentFYPlanDetails?.actualYear;
        const isCurrentFYClosed =
          currentFYPlanDetails?.status?.toLowerCase() === "closed";

        const filteredPlans = (resp || []).filter((p: any) => {
          // Previous FYs + Current FY always visible
          if (p.actualYear <= currentFY) {
            return true;
          }

          return isCurrentFYClosed;
        });

        // Map API response so Dropdown receives { label, value }
        const mapped = (filteredPlans || []).map((p: any) => {
          const normalizedStatus = (p.status ?? p.state ?? "")
            .toString()
            .trim()
            .toLowerCase();
          const baseLabel = p.planName ?? p.displayLabel ?? p.label;
          const statusLabel =
            normalizedStatus === "closed" ? "Closed" : "Active";

          return {
            label: `${baseLabel} (${statusLabel})`,
            value: p.fiscalYearId ?? p.value ?? p.year,
            status: normalizedStatus,
            actualYear: Number(p.actualYear),
            isCurrentYearPlan: p.isCurrentYearPlan,
          };
        });

        setPlans(mapped);

        if (currentFiscalYearId) {
          const urlMatch = mapped.find((p) => p.value === currentFiscalYearId);

          // fiscalYearId not present in dropdown/API response
          // => broken route scenario
          // let existing useBrokenLinkDialog flow handle it
          if (!urlMatch) {
            setThroughWCvisible(true);
            showDialog();
            return;
          }

          const currentFYPlanForUrl = mapped.find(
            (p: any) => p.isCurrentYearPlan,
          );
          
          const isExplicitPlanChoice =
            !closureTriggeredRef.current &&
            (isManualSelection ||
              (currentFYPlanForUrl &&
                urlMatch.value !== currentFYPlanForUrl.value));

          if (isExplicitPlanChoice) {
            setSelectedPlan(urlMatch.value);
            return;
          }
        }
        
        closureTriggeredRef.current = false;

        const currentFYPlan = mapped.find((p) => p.isCurrentYearPlan);

        let selectedPlanObj: any = null;

        if (currentFYPlan) {
          // Case 1:
          // prior year plan is open (not closed) -> default to it
          const priorYearPlan = mapped.find(
            (p: any) => p.actualYear === currentFYPlan.actualYear - 1,
          );
          const priorYearStatus = (priorYearPlan?.status ?? "")
            .toString()
            .trim()
            .toLowerCase();

          const currentFYStatus = (currentFYPlan.status ?? "")
            .toString()
            .trim()
            .toLowerCase();

          if (priorYearPlan && priorYearStatus !== "closed") {
            selectedPlanObj = priorYearPlan;
          } else if (currentFYStatus !== "closed") {
            // Case 2:
            // current FY itself is open
            selectedPlanObj = currentFYPlan;
          } else {
            // Case 3:
            // current FY closed
            // keep checking next years (+1, +2, +3...)
            let nextYear = currentFYPlan.actualYear + 1;

            while (!selectedPlanObj) {
              const nextPlan = mapped.find((p) => p.actualYear === nextYear);

              if (!nextPlan) {
                break;
              }

              if (nextPlan.status !== "closed") {
                selectedPlanObj = nextPlan;
                break;
              }

              nextYear++;
            }
          }
        }

        if (!selectedPlanObj && mapped.length > 0) {
          selectedPlanObj = mapped[0];
        }

        if (selectedPlanObj) {
          setIsManualSelection(false);

          setSelectedPlan(selectedPlanObj.value);

          // const shId =
          //   shareholderId ||
          //   shareholderEntraIDForSLL ||
          //   (typeof userId === "string" ? userId : null);

          if (
            !location.pathname.startsWith("/evaluationPlan") ||
            !shareholderId
          ) {
            return;
          }

          const shId =
            shareholderId ||
            currentShareholderId ||
            (typeof userId === "string" ? userId : null);

          const targetPath = shId
            ? `/evaluationPlan/${shId}/${selectedPlanObj.value}`
            : `/evaluationPlan/${selectedPlanObj.value}`;

          if (!globalThis.location.pathname.endsWith(selectedPlanObj.value)) {
            await updateFiscalYearAndNavigate(
              selectedPlanObj.value,
              targetPath,
              true,
              selectedPlanObj.actualYear,
            );
          } else {
            await updateFiscalYearAndNavigate(
              selectedPlanObj.value,
              targetPath,
              false,
              selectedPlanObj.actualYear,
            );
          }
        }
      } catch (e: any) {
        console.error("[Header] Error loading plans:", e);
        // Show error to user
        showApiError(
          "Error Loading Plans",
          e?.response?.data?.message ||
          e?.message ||
          "Unable to load fiscal year plans. Please refresh the page.",
          false,
        );
        // fallback to empty
        setPlans([]);
      }
    };

    // Only load plans when on evaluation plan page
    if (isEvaluationPlanPage) {
      loadPlans();
    }
  }, [
    shareholderId,
    userId,
    navigateTo,
    currentFiscalYearId,
    currentFY,
    isEvaluationPlanPage,
    plansReloadToken,
  ]);

  const navigate = useNavigate();

  async function updateFiscalYearAndNavigate(
    newVal: string,
    targetPath: string,
    shouldNavigate: boolean = true,
    selectedYearOverride?: number,
  ) {
    try {
      // find selected plan label to extract year (e.g. "FY 2027 Plan")
      const selectedPlanObj = plans.find((p) => p.value === newVal);

      const label = selectedPlanObj?.label || "";
      const yearMatch = label.match(/(20\d{2})/);
      const selectedYear = selectedYearOverride ?? (yearMatch ? parseInt(yearMatch[1], 10) : null);
      
      if (selectedYear) {
        SessionService.setSelectedActualYear(selectedYear);
      }

      // fetch baseline fiscal year details then override the year/id
      const baseline = await getCurrentFiscalYearDetails(accessToken);

      if (baseline && selectedYear) {
        const overridden = { ...baseline };
        const baselineYear = parseInt(baseline.actualYear, 10);
        const yearDiff = selectedYear - baselineYear;

        // Update actualYear and fiscalYearId
        overridden.actualYear = selectedYear.toString();
        overridden.fiscalYearId = newVal;

        // Adjust startDate and endDate by adding yearDiff years
        if (baseline.startDate) {
          const startDate = new Date(baseline.startDate);
          startDate.setFullYear(startDate.getFullYear() + yearDiff);
          overridden.startDate = startDate.toISOString();
        }

        if (baseline.endDate) {
          const endDate = new Date(baseline.endDate);
          endDate.setFullYear(endDate.getFullYear() + yearDiff);
          overridden.endDate = endDate.toISOString();
        }

        // Update fiscalYear string (e.g., "2026-2027")
        if (yearDiff !== 0) {
          const fyStartYear = selectedYear - 1;
          const fyEndYear = selectedYear;
          overridden.fiscalYear = `${fyStartYear}-${fyEndYear}`;
        }

        SessionService.setFiscalYearDetails(overridden);
      }
    } catch (err) {
      // ignore — navigation will still happen
      console.error(
        "[Header] Error setting fiscal year details before navigation:",
        err,
      );
    } finally {
      if (shouldNavigate) {
        // Keep SPA navigation to avoid a hard browser refresh.
        navigate(targetPath, { replace: true });
      }
    }
  }

  const handlePlanChange = (e: any) => {
    const newVal = e.value;

    // user explicitly selected dropdown value
    setIsManualSelection(true);

    setSelectedPlan(newVal);

    // Build URL for evaluation plan including shareholderId & fiscalYearId
    const shId =
      shareholderId ||
      shareholderEntraIDForSLL ||
      (typeof userId === "string" ? userId : null);
    const targetPath = shId
      ? `/evaluationPlan/${shId}/${newVal}`
      : `/evaluationPlan/${newVal}`;

    void updateFiscalYearAndNavigate(newVal, targetPath);
  };

  const handleGrowthFiscalYearChange = (e: any) => {
    const newFiscalYearId = e.value as string;
    setSelectedGrowthFiscalYearId(newFiscalYearId);

    const targetPath = isGrowthReporteePage && apexUserId
      ? `/growth/reportee/${apexUserId}/${newFiscalYearId}`
      : `/growth/${newFiscalYearId}`;

    navigate(targetPath, { replace: true });
  };

  useEffect(() => {
    if (!navigateTo && !shareholderName && !currentFY && !shLeaderName) return;
    addHeaderElementBasedOnPage();
  }, [
    navigateTo,
    shareholderName,
    currentFY,
    shLeaderName,
    isDarkMode,
    throughWCvisible,
    selectedPlan,
    plans,
    selectedGrowthFiscalYearId,
    growthFiscalYears,
    isGrowthPage,
  ]);

  useEffect(() => {
    if (isEvaluationPlanPage) {
      fetchLastRefreshDate();
    }
  }, [isEvaluationPlanPage]);

  useEffect(() => {
    // Always set document.title to match the header: {shareholderName} - FY {currentFY}
    // This ensures the browser tab always matches the header, whether it's a leader or shareholder plan
    if (isEvaluationPlanPage && shareholderName && currentFY) {
      document.title = `${shareholderName} - FY ${currentFY}`;
    }
  }, [isEvaluationPlanPage, shareholderName, currentFY]);

  const fetchSHDetails = async (
    token: any,
    shareholderId: any,
    fiscalYearId: string,
  ) => {
    try {
      const response = await getShareholderDetails(
        token,
        shareholderId,
        fiscalYearId,
      );
      if (response && response.length > 0) {
        // below context will help to set shareholder's details (whose plan is opend)
        setShareholderInfo({
          shareholderName: response[0].shareholderName,
          shareholderId: response[0].shareholderId,
        });
        setShareholderName(response[0].shareholderName);
        setLeaderName(response[0].leaderName);
        setShareholderEntraID(response[0].shareholderEntraID);
        setLeaderEntraId(response[0].leaderEntraId);
        setCurrentShareholderId(response[0].shareholderId);
      }
    } catch (error) {
      console.error("Error fetching Shareholder details:", error);
    }
  };

  const fetchLastRefreshDate = async () => {
    try {
      const result = await getLastRefreshDate();
      setLastRefreshDate(result?.lastModifiedDate);
    } catch {
      setLastRefreshDate(null);
    }
  };

  const addHeaderElementBasedOnPage = () => {
    const elements: Array<{ element: JSX.Element; key: string }> = [
      {
        element: (
          <img
            src={Apexlogo}
            alt="logo"
            className="lbmc-header-full mr-2 mt-1"
            height="30px"
            onClick={() => (window.location.href = "/")}
            style={{ cursor: "pointer" }}
          />
        ),
        key: "logo",
      },
    ];

    if (isEvaluationPlanPage || isGrowthPage) {
      elements.push(
        {
          element: <span className="separator  mr-2" />,
          key: "sep1",
        },
        {
          element: !throughWCvisible ? (
            <div className="flex align-items-center gap-2">
              {shareholderEntraIDForSLL ? (
                <MGTProfileCard
                  key={shareholderEntraIDForSLL}
                  userId={shareholderEntraIDForSLL}
                  height="25px"
                  className="rounded-circle"
                />
              ) : null}
              <span className="text-16">{shareholderName}</span>
              {/* {isGrowthPage && currentFY ? (
                <span className="text-16">FY {currentFY} Plan</span>
              ) : null} */}
            </div>
          ) : (
            <></>
          ),
          key: "user",
        },
      );
    }

    if (isEvaluationPlanPage) {
      elements.push(
        {
          element: <span className="separator ml-2" />,
          key: "sep2",
        },
        {
          element: (
            <div className="flex align-items-center ml-2 fy-container">
              <Dropdown
                value={selectedPlan}
                options={plans}
                onChange={handlePlanChange}
                optionLabel="label"
                optionValue="value"
                className="text-14 fy-dropdown"
                placeholder={currentFY ? `FY ${currentFY} Plan` : "Select Plan"}
              />
            </div>
          ),
          key: "fyplan",
        },
      );
    }

    if (isGrowthPage) {
      elements.push(
        {
          element: <span className="separator ml-2" />,
          key: "growth-sep",
        },
        {
          element: (
            <div className="flex align-items-center ml-2 fy-container">
              <Dropdown
                value={selectedGrowthFiscalYearId}
                options={growthFiscalYears}
                onChange={handleGrowthFiscalYearChange}
                optionLabel="label"
                optionValue="value"
                className="text-14 fy-dropdown"
                placeholder={currentFY ? `FY ${currentFY} Plan` : "Select Plan"}
              />
            </div>
          ),
          key: "growth-fyplan",
        },
      );
    }
    setHeaderElements(elements);
  };
  const rightContent = (
    <>
      {isEvaluationPlanPage || isGrowthPage ? (
        <>
          <span className="text-14 font-italic mr-3">
            {lastRefreshDate
              ? `Data refreshed ${moment
                .utc(lastRefreshDate)
                .local()
                .fromNow()}`
              : ""}
          </span>

          <span className="text-14 mr-1 flex align-items-center gap-2">
            Leader:
            {!throughWCvisible ? (
              <div className="flex align-items-center gap-2">
                {(isGrowthPage && !isGrowthReporteePage
                  ? growthLeaderEntraId
                  : leaderEntraIdForSLL) ? (
                  <MGTProfileCard
                    key={isGrowthPage && !isGrowthReporteePage
                      ? growthLeaderEntraId!
                      : leaderEntraIdForSLL!}
                    userId={isGrowthPage && !isGrowthReporteePage
                      ? growthLeaderEntraId!
                      : leaderEntraIdForSLL!}
                    height="25px"
                    className="rounded-circle"
                  />
                ) : null}
                {isGrowthPage && !isGrowthReporteePage
                  ? growthLeaderName
                  : shLeaderName}
              </div>
            ) : (
              <></>
            )}
          </span>
        </>
      ) : null}
    </>
  );
  return (
    <LBMCHeader
      items={headerElements}
      rightContent={
        <div className="flex align-items-center">{rightContent}</div>
      }
    />
  );
}
